/**
 * @fileoverview A abordagem inteligente de um ticket (a última, com as
 * mensagens), para a faixa da conversa e o modal de templates. Relê quando
 * chega o aviso da abordagem deste ticket (socket da empresa, que só traz ids e
 * status; os dados vêm da API).
 */

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useTicketSmartApproach = (ticketId, enabled) => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const companyId = user?.companyId;
  // A abordagem fica junto do ticket a que pertence: ao trocar de conversa, a
  // da anterior não aparece nem por um instante.
  const [state, setState] = useState({ ticketId: null, run: null });
  // Número da última leitura: resposta atrasada não sobrescreve a mais nova.
  const requestRef = useRef(0);
  // Ticket mostrado agora: leitura pedida por outra conversa (depois de trocar) é ignorada.
  const ticketRef = useRef(ticketId);
  ticketRef.current = ticketId;

  const reload = useCallback(async () => {
    if (!ticketId || !enabled || ticketRef.current !== ticketId) return;
    const current = ++requestRef.current;
    const isCurrent = () => current === requestRef.current && ticketRef.current === ticketId;
    try {
      const { data } = await api.get(`/tickets/${ticketId}/smart-approach`);
      if (isCurrent()) setState({ ticketId, run: data?.run || null });
    } catch (err) {
      if (!isCurrent()) return;
      // Sem acesso ou ticket sumiu: nada a mostrar. Falha momentânea: mantém o que estava.
      const status = err?.response?.status;
      if (status === 403 || status === 404) setState({ ticketId, run: null });
    }
  }, [ticketId, enabled]);

  useEffect(() => {
    if (!ticketId || !enabled || !companyId) {
      requestRef.current += 1;
      setState({ ticketId: null, run: null });
      return undefined;
    }
    reload();

    const socket = socketManager.getSocket(companyId);
    let timer = null;
    const onChange = (data) => {
      if (Number(data?.ticketId) !== Number(ticketId)) return;
      // Várias mudanças seguidas viram uma leitura só.
      clearTimeout(timer);
      timer = setTimeout(reload, 300);
    };
    socket.on(`company-${companyId}-smartApproach`, onChange);
    return () => {
      clearTimeout(timer);
      requestRef.current += 1;
      // disconnect() do socket gerenciado só solta os ouvintes deste uso (a conexão continua).
      socket.disconnect();
    };
  }, [ticketId, enabled, companyId, socketManager, reload]);

  // Depois de cancelar, a resposta já traz a abordagem atualizada: mostra na
  // hora, se ainda for a que está na tela.
  const applyRun = useCallback((next) => {
    // Leitura que saiu antes do cancelamento não volta a mostrar a abordagem ativa.
    requestRef.current += 1;
    setState((prev) => (next && prev.run && prev.run.id === next.id ? { ...prev, run: next } : prev));
  }, []);

  const run = enabled && state.ticketId === ticketId ? state.run : null;
  return { run, reload, applyRun };
};

export default useTicketSmartApproach;
